import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SelectAllCheckbox, SelectAllCheckboxContainer } from '@/core/styled';
import { Autocomplete, ClickAwayListener, createFilterOptions } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import { AutocompletePopperStyled, FilterTextField, FormControlLabelStyled, PaperHead, PaperStyled, PopperStyled, StyledDivider, } from './CheckboxSelect.styled';
import { menuPaperTopOffsetModifier } from '@/core/constants';
import { t } from '@/core/localization';
import { Checkbox } from '../Checkbox';
import { NoOptionsTypography } from '../shared.styled';
import { CheckboxSelectRootButton } from './CheckboxSelectRootButton';
/**
 * Select with checkboxes and one SelectAll checkbox that checks or unchecks only filtered options.
 *
 * Uses MuiAutocomplete under the hood.
 *
 * @param options Options list
 * @param keyField The name of the field that contains item key
 * @param labelField The name of the field that contains item label
 */
const CheckboxSelect = ({ options, keyField, labelField, popperWidth, onClose, onClear, ...controllerProps }) => {
    const [anchorElement, setAnchorElement] = useState(null);
    const [selectAll, setSelectAll] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    // Using useState instead of useRef here forces infinite rerenders on setFilterOptions.
    const filteredOptionIds = useRef([]);
    const { field: { value, onChange }, } = useController(controllerProps);
    const handleClose = () => {
        if (anchorElement) {
            anchorElement.focus();
        }
        setAnchorElement(null);
    };
    const onSelectAllCheckboxClick = useCallback(() => {
        if (selectAll) {
            onChange(value.filter((id) => !filteredOptionIds.current.some((optionId) => optionId === id)));
        }
        else {
            const idsToAdd = filteredOptionIds.current.filter((optionId) => !(value ?? []).some((id) => id === optionId));
            onChange([...(value ?? []), ...idsToAdd]);
        }
    }, [value, selectAll, onChange]);
    const selectedValues = useMemo(() => (value ? options.filter((option) => value.includes(option[keyField])) : []), [keyField, options, value]);
    const filter = createFilterOptions();
    return (_jsxs(_Fragment, { children: [_jsx(CheckboxSelectRootButton, { selectedItemsCount: value?.length, onClick: (event) => setAnchorElement(event.currentTarget), onClearButtonClick: () => onChange([]) }), _jsx(PopperStyled, { disablePortal // allows width to be equal to container
                : true, 
                // keepMounted
                open: Boolean(anchorElement), anchorEl: anchorElement, placement: "bottom-start", modifiers: [
                    {
                        name: 'offset',
                        options: { offset: menuPaperTopOffsetModifier },
                    },
                ], width: popperWidth, children: _jsx(ClickAwayListener, { onClickAway: handleClose, children: _jsx(PaperStyled, { children: _jsx(Autocomplete, { open: true, multiple: true, disableClearable: true, value: selectedValues, onClose: (event, reason) => {
                                onClose?.(event, reason);
                                if (reason === 'escape') {
                                    handleClose();
                                }
                            }, onChange: (event, newValue, reason) => {
                                if (reason === 'clear')
                                    onClear?.();
                                const selectedIds = newValue.map((item) => item[keyField]);
                                if (reason === 'clear' || reason === 'removeOption')
                                    setSelectAll(false);
                                if (reason === 'selectOption' && filteredOptionIds.current.every((optionId) => selectedIds.includes(optionId)))
                                    setSelectAll(true);
                                onChange(selectedIds);
                            }, 
                            // Fix bug when Backspace or Delete button press removes selection from checkbox list.
                            onKeyDown: (event) => {
                                if (event.key === 'Backspace' || event.key === 'Delete') {
                                    event.defaultMuiPrevented = true;
                                }
                            }, disableCloseOnSelect: true, renderValue: () => null, noOptionsText: _jsx(NoOptionsTypography, { children: t('nothing.is.found.label') }), renderOption: (props, option, { selected }) => (_createElement("li", { ...props, key: option[keyField] },
                                _jsx(Checkbox, { checked: selected, tabIndex: -1 }),
                                option[labelField])), options: options ?? [], getOptionLabel: (option) => option[labelField], filterOptions: (options, state) => {
                                const filtered = filter(options, state);
                                filteredOptionIds.current = filtered.map((option) => option[keyField]);
                                // TODO: refactor `selectAll` at all (see CheckboxAutocomplete (and CheckboxList), same logic)
                                const allFilteredAreChecked = filteredOptionIds.current.every((optionId) => value.includes(optionId));
                                if (selectAll !== allFilteredAreChecked) {
                                    // setTimeout is used to prevent console error `Cannot update a component while rendering a different component`
                                    setTimeout(() => {
                                        setSelectAll(allFilteredAreChecked);
                                    }, 10);
                                }
                                return filtered;
                            }, 
                            // groupBy={(option) => option.city}
                            defaultValue: [], inputValue: searchInputValue, onInputChange: (_, value, reason) => {
                                if (reason === 'input' || reason === 'clear')
                                    setSearchInputValue(value);
                            }, renderInput: (params) => (_jsxs(PaperHead, { children: [_jsx(FilterTextField, { autoFocus: true, placeholder: t('filter.label'), ref: params.slotProps.input.ref, 
                                        // type="search" (brings native clear xMark to input[type=text])
                                        slotProps: {
                                            ...params.slotProps,
                                            htmlInput: { ...params.slotProps.htmlInput, type: 'search' },
                                        } }), _jsx(SelectAllCheckboxContainer
                                    //onMouseDown={(event) => event.preventDefault()} // prevents blur
                                    , { children: filteredOptionIds.current.length > 0 && (_jsx(FormControlLabelStyled
                                        // onClick handler used instead of Checkbox onChange since there happens bug when click not on
                                        // checkbox icon itself but on label.
                                        , { 
                                            // onClick handler used instead of Checkbox onChange since there happens bug when click not on
                                            // checkbox icon itself but on label.
                                            onClick: (event) => {
                                                event.preventDefault(); // prevent blur
                                                onSelectAllCheckboxClick();
                                            }, label: t('select.all.label'), control: _jsx(SelectAllCheckbox, { checked: selectAll }) })) }), _jsx(StyledDivider, {})] })), slots: { popper: PopperComponent }, slotProps: { popupIndicator: { sx: { display: 'none' } } } }) }) }) })] }));
};
const PopperComponent = (props) => {
    const { disablePortal, anchorEl, open, ...other } = props;
    // TODO: to delete later (it was used to override inline mui styles)
    // return <AutocompletePopperStyled {...other} style={{ borderRadius: 0, boxShadow: 'none' }} />;
    return _jsx(AutocompletePopperStyled, { ...other });
};
export { CheckboxSelect };
